import _ from 'lodash';

import {
  addParamsToUrl,
  deduplicateSlashes,
  ensureTrailingSlash,
  stripTrailingSlash,
} from '@zapier/url-utils';

import createCache from 'app/entity/utils/createCache';
import { deleteJson, getJson, patchJson, postJson } from 'app/common/fetch';

const defaultFormatUrl = (baseUrl, path) => `${baseUrl}/${path}`.trim();

const stripSlashes = _.flow(
  deduplicateSlashes,
  stripTrailingSlash
);

const stripSlashesExceptLast = _.flow(
  deduplicateSlashes,
  ensureTrailingSlash
);

const hasValidCacheConfig = options => {
  const requiredProps = [
    'setItem',
    'getItem',
    'getCollection',
    'setCollection',
  ];

  return requiredProps.every(prop => {
    const check = _.get(options, ['cache', prop]);
    return !_.isUndefined(check) && _.isFunction(check);
  });
};

const defaultOptions = {
  shouldCache: false,
  cache: {},
};

const createEntityCache = (entityType, baseUrl, legacyApis) => {
  const store = createCache(entityType, baseUrl, legacyApis);

  return {
    getItem: key => store.then(cache => cache.getItem(key)),
    setItem: (key, data) => store.then(cache => cache.setItem(key, data)),
    getCollection: () => store.then(cache => cache.getCollection()),
    setCollection: collection =>
      store.then(cache => cache.setCollection(collection)),
  };
};

const buildEntityApi = (
  entityType,
  baseUrl,
  customize = _.identity,
  options = {}
) => {
  const opts = {
    ...defaultOptions,
    ...(options.shouldCache === true && !options.cache
      ? { cache: createEntityCache(entityType, baseUrl, options.legacyApis) }
      : {}),
    ...options,
  };

  if (opts.shouldCache === true && !hasValidCacheConfig(opts)) {
    throw new Error(
      `buildEntityApi for the entity ${entityType} has been provided an invalid cache configuration.`
    );
  }

  if (opts.shouldCache === true && !_.isString(baseUrl)) {
    throw new Error(
      `buildEntityApi for the entity ${entityType} has been provided an invalid baseUrl.`
    );
  }

  const formatUrl = _.isFunction(baseUrl)
    ? baseUrl
    : _.partial(defaultFormatUrl, baseUrl);
  const handleSlashes = options.ensureTrailingSlash
    ? stripSlashesExceptLast
    : stripSlashes;
  const api = {
    collectionEndpoint: (path = '', getState) =>
      handleSlashes(formatUrl(path, getState)),
    modelEndpoint: (id, getState) =>
      handleSlashes(api.collectionEndpoint(id, getState)),
  };

  if (opts.shouldCache === true) {
    api.loadItemFromCache = id => opts.cache.getItem(id);
    api.saveItemToCache = (id, value) => opts.cache.setItem(id, value);
    api.loadCollectionFromCache = () => {
      return opts.cache.getCollection().then(data => {
        // Need to return the data in the shape that the API responds with
        return {
          objects: data,
        };
      });
    };
    api.saveCollectionToCache = collection =>
      opts.cache.setCollection(collection);
  }

  // Highly customizable API functions
  api.createWith = (transformUrl, data, getState) =>
    postJson(transformUrl(api.collectionEndpoint(undefined, getState)), data);

  api.deleteWith = (transformUrl, id, data, getState) =>
    deleteJson(transformUrl(api.modelEndpoint(id, getState)), data);

  api.loadWith = (transformUrl, id, getState) =>
    getJson(transformUrl(api.modelEndpoint(id, getState)));

  api.saveWith = (transformUrl, id, delta, getState) =>
    patchJson(transformUrl(api.modelEndpoint(id, getState)), delta);

  api.loadCollectionWith = (transformUrl, getState) => {
    return getJson(
      transformUrl(api.collectionEndpoint(undefined, getState))
    ).then(data => {
      if (opts.shouldCache === true) {
        api.saveCollectionToCache(data.objects || data.results);
      }

      return data;
    });
  };

  api.saveCollectionWith = (transformUrl, delta, getState) =>
    patchJson(transformUrl(api.collectionEndpoint(undefined, getState)), delta);

  // Default API functions
  api.create = _.partial(api.createWith, _.identity);
  api.delete = _.partial(api.deleteWith, _.identity);
  api.load = _.partial(api.loadWith, _.identity);
  api.save = _.partial(api.saveWith, _.identity);
  api.loadCollection = _.partial(api.loadCollectionWith, _.identity);
  api.saveCollection = _.partial(api.saveCollectionWith, _.identity);

  // API functions that support query params.
  const withParams = apiMethod => (params, ...args) =>
    apiMethod(url => addParamsToUrl(url, params), ...args);
  api.createWithParams = withParams(api.createWith);
  api.loadWithParams = withParams(api.loadWith);
  api.saveWithParams = withParams(api.saveWith);
  api.loadCollectionWithParams = withParams(api.loadCollectionWith);

  customize(api);
  return api;
};

export default buildEntityApi;
